<svelte:options accessors />

<script context="module" lang="ts">
	export interface IDealer {
		street: null;
		state: any;
		id: string;
		name: string;
		city: string;
		zipcode: string;
		/** inKD will only be populated for if dup checking is enabled*/
		inKD: boolean;
		phone: string;
		website: string;
		ErpExtId: string;
	}
	interface CustomSelectEvent extends Event {
		value: IDealer;
	}
</script>

<script lang="ts">
	import { onMount, tick } from 'svelte';
	import type Modal from '$lib/client/components/Modal.svelte';
	import type { Component, DealerSelector } from '@prisma/client';
	import * as Store from '$lib/client/store';
	import { formURL } from '$lib/client/store';
	import { REST } from '$lib/client/conn';
	import { isUSAStateZip } from '$lib/utility';
	import { KTIForm, ServerDataValidation } from '$lib/client/form';
	import { browser } from '$app/environment';
	import { adjustNotchWidth } from '../utility';
	import { FormController } from '../actions';
	import Autocomplete from '../sub_components/Autocomplete.svelte';
	import { config } from 'process';
	import FormGroup from '../sub_components/FormGroup.svelte';

	export let Valid = false;
	export let Value: string = '';
	//export let Config.allowPOBoxes: boolean;
	export let Config: DealerSelector & Component;
	export let SaveData = true;
	let Shown = Config.Shown;

	//export let FormURL: string;
	let DealerObjValue: IDealer;
	let showAddressInput = false;
	let dealers: IDealer[] = [];
	let zipcodeInputEl: HTMLInputElement;
	let zipcodeInputOutlineEl: HTMLDivElement;
	let dealerSelectOutlineEl: HTMLDivElement;
	let dealerListSelectEl: HTMLInputElement;
	let dealerListAutocompleteObj: any;
	let isInvalid = false;
	let isValid = false;
	let activeClass = false;
	let mdb: any;
	let selectedDealer: IDealer;
	let defaultZipcodeErrMsg = 'Enter a postal code to select a dealership';
	let zipcodeErrMsg = 'Enter a postal code to select a dealership';
	let dealerSelectErrMsg = '';
	let lastZipSearch = '';
	let zipcodeErrorEl: HTMLDivElement;
	let autocompleteOpen = false;
	let notchMiddleEl_zip: HTMLDivElement;
	let labelEl_zip: HTMLLabelElement;
	let notchMiddleEl_dealer: HTMLDivElement;
	let labelEl_dealer: HTMLLabelElement;
	let serverValidation: ServerDataValidation | null = !Config.AllowDuplicateEntries
		? KTIForm.addServerValidation('dealer', Config.id)
		: null;

	const dealerFilter = (value: string) => {
		console.log(dealers.length);
		return dealers.filter((dealer) => {
			return (
				dealer?.name?.toLowerCase().includes(value?.toLowerCase()) ||
				dealer?.city?.toLowerCase().includes(value?.toLowerCase()) ||
				dealer?.zipcode?.toLowerCase().includes(value?.toLowerCase())
			);
		});
	};

	let handlersCreated = false;

	export function show() {
		Config.Shown = true;
		Shown = true;
		SaveData = true;
	}
	export function hide() {
		Config.Shown = false;
		Shown = false;
		SaveData = false;
	}

	function autocompleteOpenHandler(e: Event) {
		console.log('autocompleteOpenHandler');
		autocompleteOpen = true;
		if (Config.DealerNameId) {
			FormController.set_visibility(Config.DealerNameId, false);
		}
		if (Config.PhoneId) {
			FormController.set_visibility(Config.PhoneId, false);
		}
		if (Config.Address2Id) {
			FormController.set_visibility(Config.Address2Id, false);
		}
		if (Config.WebsiteId) {
			FormController.set_visibility(Config.WebsiteId, false);
		}
		if (Config.InfoFooterId) {
			FormController.set_visibility(Config.InfoFooterId, false);
		}
	}

	//Note that during the dealer lookup process, the dealer address is already checked for duplicates
	// Other related addresses are not
	export async function doServerValidation() {
		if (serverValidation && Config.Address2Id) {
			let address = FormController.get_component(Config.Address2Id);
			if (!address) {
				console.error('Address component not found');
				return;
			}
			let addressComp = address.Comp;
			let address_street_field = address.DBCol + 'street';
			let address_city_field = address.DBCol + 'city';
			let address_state_field = address.DBCol + 'state';
			let address_zipcode_field = address.DBCol + 'zipcode';
			let data_packet = {
				street: selectedDealer.street,
				city: selectedDealer.city,
				state: selectedDealer.state,
				zipcode: selectedDealer.zipcode,
				street_fields: address.DBCol + 'street',
				city_fields: address.DBCol + 'city',
				state_fields: address.DBCol + 'state',
				zipcode_fields: address.DBCol + 'zipcode'
			};
			let valid = await serverValidation.validate_value(data_packet);
			if (KTIForm.form_config.ShowImmediateEmailPhoneAddressErrors) {
				if (!valid) {
					setInvalid();
					KTIForm.error_modal.Show(
						'This address has already been used. This is a one-time direct order from King Technology with a maximum purchase of 6 Algae Blocker 4Pro Buckets per dealer location'
					);
				}
			}
		}
	}

	let dealerNotFound_Selected = false;
	function autocompleteSelectHandler(e: Event) {
		dealerListSelectEl.disabled = true;
		let itemSelectEvent = e as CustomSelectEvent;
		if (itemSelectEvent.value.id == 'Dealer Not Found') {
			if (!Config.AllowManualEntry && Config.DealerNotFoundOption) {
				dealerNotFound_Selected = true;
				setValid();
				return;
			}
			if (serverValidation) {
				serverValidation.validated = true;
				serverValidation.valid = true;
			}
			Value = '';
			dealerNotFound_Selected = true;
			DealerObjValue = null;
			//Clear and show Phone, Address and Website fields
			if (Config.DealerNameId) {
				FormController.set_value(Config.DealerNameId, null);
				FormController.make_enabled(Config.DealerNameId);
				FormController.set_visibility(Config.DealerNameId, true);
				FormController.set_saved(Config.DealerNameId, true);
			}
			if (Config.PhoneId) {
				FormController.set_value(Config.PhoneId, null);
				FormController.make_enabled(Config.PhoneId);
				FormController.set_visibility(Config.PhoneId, true);
				FormController.set_saved(Config.PhoneId, true);
			}
			if (Config.Address2Id) {
				FormController.set_value(Config.Address2Id, null);
				FormController.make_enabled(Config.Address2Id);
				FormController.set_visibility(Config.Address2Id, true);
				FormController.set_saved(Config.Address2Id, true);
			}
			if (Config.WebsiteId) {
				FormController.set_value(Config.WebsiteId, null);
				FormController.make_enabled(Config.WebsiteId);
				FormController.set_visibility(Config.WebsiteId, true);
				FormController.set_saved(Config.WebsiteId, true);
			}

			if (Config.CenPOSId) {
				const CenPOSComp = FormController.get_component(Config.CenPOSId).Comp;
				CenPOSComp.setCustomerID(null);
			}
			return;
		} else {
			dealerNotFound_Selected = false;
			if (serverValidation) {
				serverValidation.validated = false;
				serverValidation.valid = false;
			}
		}
		let dealerRecord = itemSelectEvent.value;
		let dealerID = dealerRecord.id;
		selectedDealer = dealers.find((dealer) => dealer.id == dealerID);
		if (selectedDealer?.inKD) {
			KTIForm.error_modal.SetTmpTitle('Dealer is Already Enrolled');
			KTIForm.error_modal
				.Show(
					Config.DuplicateMessage ??
						'This dealership has already been enrolled in this program. Check that you have selected the correct dealership and contact our Customer Service team at 800-222-0169 or <a href="mailto:customer.service@kingtechnology.com">customer.service@kingtechnology.com</a>.'
				)
				.then(() => {
					//Clear the selected dealer
					setInvalid(undefined, 'Please select a different dealer');
				});
			return;
		}
		//If related address then check address against the address fields
		if (Config.Address2Id) {
			doServerValidation();
		}
		Value = dealerID;
		DealerObjValue = dealerRecord;
		//Update and show Name, Phone, Address and Website fields
		if (Config.DealerNameId) {
			//Only show if dealer name 'Dealer Not Found' is selected
			if (dealerRecord.name == null || dealerRecord.name == '') {
				FormController.set_value(Config.DealerNameId, null);
				FormController.make_enabled(Config.DealerNameId);
				FormController.set_saved(Config.DealerNameId, true);
				FormController.set_visibility(Config.DealerNameId, true);
			} else {
				FormController.set_visibility(Config.DealerNameId, false);
				FormController.set_value(Config.DealerNameId, dealerRecord.name);
				FormController.make_disabled(Config.DealerNameId);
				FormController.set_saved(Config.DealerNameId, false);
			}
		}

		if (Config.PhoneId) {
			//If phone number is set
			if (dealerRecord.phone != null && dealerRecord.phone != '') {
				try {
					//Set the phone number and make it readonly
					FormController.set_value(Config.PhoneId, dealerRecord.phone);
					FormController.make_disabled(Config.PhoneId);
					FormController.set_saved(Config.PhoneId, false);
				} catch (e) {
					console.error('Error setting phone number', e);
				}
			} else {
				FormController.set_value(Config.PhoneId, null);
				FormController.make_enabled(Config.PhoneId);
				FormController.set_saved(Config.PhoneId, true);
			}
			FormController.set_visibility(Config.PhoneId, true);
		}
		if (Config.Address2Id) {
			//If address is set
			if (dealerRecord.street != null && dealerRecord.street != '') {
				try {
					//Set the address and make it readonly
					FormController.set_value(Config.Address2Id, {
						street: dealerRecord.street,
						city: dealerRecord.city,
						state: dealerRecord.state,
						zipcode: dealerRecord.zipcode
					});
					FormController.make_disabled(Config.Address2Id);
					FormController.set_saved(Config.Address2Id, false);
				} catch (e) {
					console.error('Error setting address', e);
				}
			} else {
				FormController.set_value(Config.Address2Id, null);
				FormController.make_enabled(Config.Address2Id);
				FormController.set_saved(Config.Address2Id, true);
			}
			FormController.set_visibility(Config.Address2Id, true);
		}
		if (Config.WebsiteId) {
			//If website is set
			if (dealerRecord.website != null && dealerRecord.website != '') {
				try {
					//Set the website and make it readonly
					FormController.set_value(Config.WebsiteId, dealerRecord.website);
					FormController.make_disabled(Config.WebsiteId);
					FormController.set_saved(Config.WebsiteId, false);
				} catch (e) {
					console.error('Error setting website', e);
				}
			} else {
				FormController.set_value(Config.WebsiteId, null);
				FormController.make_enabled(Config.WebsiteId);
				FormController.set_saved(Config.WebsiteId, true);
			}
			FormController.set_visibility(Config.WebsiteId, true);
		}
		if (Config.InfoFooterId) {
			FormController.set_visibility(Config.InfoFooterId, true);
		}
		setValid();
	}

	function autocompleteCloseHandler(e: Event) {
		console.log('autocompleteCloseHandler()');
		autocompleteOpen = false;
		if (selectedDealer?.inKD) {
			return;
		}
	}

	function createDealerAutocomplete() {
		console.log('createDealerAutocomplete()');
		if (dealerListAutocompleteObj && dealerListAutocompleteObj.dispose) {
			try {
				dealerListAutocompleteObj.dispose();
			} catch (error) {
				console.log('Error disposing dealerListAutocompleteObj', error);
			}
		}
		dealerListAutocompleteObj = new mdb.Autocomplete(dealerSelectOutlineEl, {
			filter: dealerFilter,
			debounce: 300,
			displayValue: (value: IDealer) => value?.name,
			itemContent: (result: IDealer) => {
				let subtitle = '';
				if (result?.city != null) {
					subtitle += result.city + ' ';
				}
				if (result?.zipcode != null) {
					subtitle += result.zipcode;
				}
				return `
                <div class="autocomplete-custom-item-content">
                <div class="autocomplete-custom-item-title">${result?.name}</div>
                <div class="autocomplete-custom-item-subtitle">${subtitle}</div>
                </div>
            `;
			}
		});
		if (!handlersCreated) {
			dealerSelectOutlineEl.addEventListener('open.mdb.autocomplete', autocompleteOpenHandler);
			dealerSelectOutlineEl.addEventListener(
				'itemSelect.mdb.autocomplete',
				autocompleteSelectHandler
			);
			dealerSelectOutlineEl.addEventListener('close.mdb.autocomplete', autocompleteCloseHandler);
		}
		handlersCreated = true;
	}

	let dealerSelectOutlineObj: any;
	onMount(async () => {
		Store.eraseDealer.set(() => {
			selectedDealer.inKD = true;
			dealerListSelectEl.value = '';
			dealerListSelectEl.focus();
			Value = '';
			setNuetral();
		});
		adjustNotchWidth(labelEl_zip, notchMiddleEl_zip);
		mdb = await import('mdb-ui-kit');
		// const { Input } = await import('mdb-ui-kit');
		// //let one = new Input(dealerListSelectEl).init();
		// new Input(zipcodeInputOutlineEl).init();
		createDealerAutocomplete();
	});
	let loadingDealerInfo = {
		loading: false,
		zipcode: '',
		startTime: 0
	};
	function updateDealerList() {
		createDealerAutocomplete();
		tick().then(() => {
			dealerListSelectEl.value = '';
			dealerListSelectEl.focus();
			if (!dealerSelectOutlineObj) {
				dealerSelectOutlineObj = new mdb.Input(dealerSelectOutlineEl).init();
			}
		});
	}
	async function getDealers(zipcode: string) {
		if (
			loadingDealerInfo.loading &&
			loadingDealerInfo.zipcode == zipcode &&
			Date.now() - loadingDealerInfo.startTime < 1000
		) {
			return;
		}
		if (dealerListAutocompleteObj && dealerListAutocompleteObj.dispose) {
			try {
				dealerListAutocompleteObj.dispose();
			} catch (error) {
				console.log('Error disposing dealerListAutocompleteObj', error);
			}
		}
		loadingDealerInfo = {
			loading: true,
			zipcode,
			startTime: Date.now()
		};
		const queryParams = new URLSearchParams({
			compID: Config.id.toString(),
			zipcode
			// allowRedMAP: 'false',
			// allowPOBoxes: allowPOBoxes ? 'true' : 'false',
			// allowOCONUS: allowOCONUS ? 'true' : 'false',
			// allowDuplicateEntries: allowDuplicateEntries ? 'true' : 'false',
			// brandFilter: BrandFilter.join('|')
		});
		// let referer = window.location.href;
		// if (window.location.href.indexOf($formURL) < 1) {
		// 	referer = window.location.href + 'form/' + $formURL;
		// }
		const res = await REST.GET('dealers', {
			compID: Config.id.toString(),
			zipcode
		});
		if (res.ok) {
			lastZipSearch = zipcode;
			Value = '';
			dealers = await res.json();
			if (Config.AllowManualEntry || Config.DealerNotFoundOption) {
				dealers.push({
					id: 'Dealer Not Found',
					name: 'Dealer Not Found',
					city: '',
					zipcode: '',
					inKD: false,
					ErpExtId: ''
				});
			}
			updateDealerList();
		} else {
			dealers = [];
			updateDealerList();
			let msg = (await res.json())?.message;
			console.log(msg);
			console.error(`Error fetching dealers: ${res.status} ${res.statusText}`);
			if (msg) {
				zipcodeErrMsg = msg;
			} else {
				zipcodeErrMsg = 'Invalid US Postal Code';
			}
			isInvalid = true;
		}

		loadingDealerInfo.loading = false;
		adjustNotchWidth(labelEl_dealer, notchMiddleEl_dealer);

		// await fetch('/mc/get_dealers/' + FormURL, {
		// 	method: 'post',
		// 	headers: { 'Content-Type': 'application/json' },
		// 	body: JSON.stringify({ state: stateAbbr })
		// })
		// 	.then((data) => {
		// 		LoadingFSOff();
		// 		data.json().then((data) => {
		// 			if ('error' in data) {
		// 				showError(data.error);
		// 				console.log('data', data);
		// 				dealers = [];
		// 			} else {
		// 				dealers = data;
		// 			}
		// 			dealerListAutocompleteObj.dispose();
		// 			dealerListAutocompleteObj = new mdb.Autocomplete(dealerListAutocomplete, {
		// 				filter: dealerFilter,
		// 				displayValue: (value) => value?.name,
		// 				itemContent: (result) => {
		// 					let subtitle = '';
		// 					if (result?.city != null) {
		// 						subtitle += result.city + ' ';
		// 					}
		// 					if (result?.zipcode != null) {
		// 						subtitle += result.zipcode;
		// 					}
		// 					return `
		//                 <div class="autocomplete-custom-item-content">
		//                 <div class="autocomplete-custom-item-title">${result?.name}</div>
		//                 <div class="autocomplete-custom-item-subtitle">${subtitle}</div>
		//                 </div>
		//             `;
		// 				}
		// 			});
		// 			$('#dealerListSelect').focus();
		// 			dealerListAutocompleteObj.initSearch('');
		// 			$('#dealerListSelect').focus();
		// 		});
		// 	})
		// 	.catch((err) => {
		// 		LoadingFSOff();
		// 	});
	}
	let showDealers = false;
	let zipcodeValid = false;
	function handleZipcodeInput(e: Event & { currentTarget: EventTarget & HTMLInputElement }) {
		//reset everything
		isInvalid = false;
		isValid = false;
		zipcodeValid = false;
		//remove everything except for digits
		zipcodeInputEl.value = zipcodeInputEl.value.replace(/\D/g, '');
		if (zipcodeInputEl.value.length > 5) {
			zipcodeInputEl.value = zipcodeInputEl.value.slice(0, 5);
		}
		let zipcode = zipcodeInputEl.value;
		if (zipcode.length == 5) {
			if (!isUSAStateZip(zipcode)) {
				if (Config.Required) {
					console.log('setting invalid');
					setInvalid('Please enter valid US Postal Code');
					return;
				}
			}
			zipcodeValid = true;
			showDealers = true;
			// if (lastZipSearch == zipcode) {
			// 	console.log('using cached dealers', dealers);
			// 	updateDealerList();
			// 	return;
			// }
			lastZipSearch = zipcode;
			getDealers(zipcode);
		} else {
			Valid = false;
			Value = '';
			showDealers = false;
		}
	}
	function handleZipcodeBlur(e: Event & { currentTarget: EventTarget & HTMLInputElement }) {
		handleZipcodeInput(e);
		if (!zipcodeValid) {
			setInvalid('Please enter a valid US Postal Code');
		}
	}
	export function setNuetral() {
		isInvalid = false;
		isValid = false;
	}
	export function setValid() {
		Valid = true;
		isInvalid = false;
		isValid = true;
		//if we have a dealer ID send it to CenPOS
		if (Value && Config.CenPOSId) {
			const CenPOSComp = FormController.get_component(Config.CenPOSId).Comp;
			CenPOSComp.setCustomerID(Value);
		}
	}
	export function setInvalid(errMsg?: string, selectorErrMsg?: string) {
		if (errMsg) {
			zipcodeErrMsg = errMsg;
		} else {
			zipcodeErrMsg = defaultZipcodeErrMsg;
		}
		if (selectorErrMsg) {
			dealerSelectErrMsg = selectorErrMsg;
		} else {
			dealerSelectErrMsg = 'Please select a dealer';
		}
		Valid = false;
		isInvalid = true;
		isValid = false;
	}
	$: showDealerSelector = !loadingDealerInfo.loading && dealers && dealers.length > 0;
	$: showZipCodeError = isInvalid && !showDealerSelector;
	export function focus() {
		if (showDealerSelector) {
			dealerListSelectEl.focus();
		} else {
			zipcodeInputEl.focus();
		}
	}
	export async function validate() {
		if (!Config.Shown) {
			setValid();
			return true;
		}
		//wait a tick
		await tick();
		//Wait until loadingDealers is false
		while (loadingDealerInfo.loading) {
			await new Promise((r) => setTimeout(r, 100));
		}
		if ((showDealerSelector && Value != '') || dealerNotFound_Selected) {
			Valid = true;
			setValid();
		} else if (Config.Required) {
			Valid = false;
			setInvalid();
		} else {
			Valid = false;
			setNuetral();
		}
		return Valid;
	}
	function getData() {
		return {
			id: Value,
			id_type: DealerObjValue.ErpExtId ? 'erp' : 'crm',
			zipcode: lastZipSearch
		};
	}
	export function appendData(formData: FormData) {
		if (!SaveData) {
			formData.append('dont_save_columns', Config.DBCol + 'phone');
			return;
		}
		if (dealerNotFound_Selected) {
			return;
		}
		if (Value != '') {
			for (let [key, value] of Object.entries(getData())) {
				console.log(`DealerSelector appendData: ${key}->${value}`);
				formData.set(Config.DBCol + key, value);
			}
		}
	}
	$: if (Valid) {
		if (Value != '') {
			for (let [key, value] of Object.entries(getData())) {
				Store.formData.setValue(key, value);
			}
		}
	}
	let zipcodeVal: string = '';
	export function dealerSelected(content: string, value: any) {
		console.log('dealerSelected', content, value);
	}
	function resetDealerSelector() {
		dealerListSelectEl.value = '';
		Value = '';
		dealerListSelectEl.disabled = false;
		// Programmatically focus the input
		dealerListSelectEl.focus();

		// Dispatch the 'input' event to trigger the dropdown
		dealerListSelectEl.dispatchEvent(new Event('input', { bubbles: true }));
	}
</script>

<div
	class="form-group row g-2 {Shown ? '' : 'hidden'}"
	id="address_row_d"
	class:required={Config.Required}
>
	<div class="comp-title">{Config.Title ?? 'Dealer'}</div>
	{#if Config.BelowTitleText}
		<div class="fs-7" style="margin-top: 0">{Config.BelowTitleText}</div>
	{/if}
	<div class="col-6" id="dealerZipcodeSelectorSection">
		<div class="col">
			<div class="form-outline" bind:this={zipcodeInputOutlineEl}>
				<input
					class="form-control"
					id="dealerZipcodeSelect"
					type="text"
					bind:this={zipcodeInputEl}
					on:input={handleZipcodeInput}
					bind:value={zipcodeVal}
					class:is-invalid={showZipCodeError}
					class:is-valid={isValid}
					class:active={zipcodeVal != ''}
					placeholder="Enter 5-digit Postal Code"
					autocomplete="section-dealer postal-code" 
				/>
				<div class="form-notch">
					<div class="form-notch-leading" style="width: 9px;" />
					<div class="form-notch-middle" style="width: 105.6px;" bind:this={notchMiddleEl_zip} />
					<div class="form-notch-trailing" />
				</div>
				<label class="form-label" for="dealerZipcodeSelect" bind:this={labelEl_zip}
					>Dealer postal code</label
				>
				<div class="autocomplete-custom-content" />
				<div class="invalid-tooltip" class:hidden={!showZipCodeError} id="dealerZipcodeSelectInfo" bind:this={zipcodeErrorEl}>
					{zipcodeErrMsg}
				</div>
			</div>
		</div>
	</div>
	{#if Config.AllowManualEntry}
		<span class="small snuggle"
			>If your dealership is not listed, select the “Dealer Not found” option and enter your
			dealership information.</span
		>
	{/if}
</div>
<!-- <div class="row">
	<div class="col-12">
		<div class="col" class:hidden={!showDealers}>
			{#if loadingDealerInfo.loading}
				<div class="spinner-border spinner-border-sm" role="status">
					<span class="sr-only">Loading...</span>
				</div>
				loading
			{/if}
			{#if showDealerSelector}
				<Autocomplete placeholder="Street" on:select />
			{/if}
		</div>
	</div>
</div> -->
<FormGroup {Config}>
	<div class="col-12">
		<div class="col" class:hidden={!showDealers}>
			{#if loadingDealerInfo.loading}
				<div class="spinner-border spinner-border-sm" role="status">
					<span class="sr-only">Loading...</span>
				</div>
				loading
			{/if}
			<div class:hidden={!showDealerSelector}>
				<div class="form-outline" bind:this={dealerSelectOutlineEl}>
					<input
						class="form-control"
						id="dealerListSelect"
						type="text"
						bind:this={dealerListSelectEl}
						disabled={loadingDealerInfo.loading}
						class:is-invalid={isInvalid &&
							!loadingDealerInfo.loading &&
							!autocompleteOpen &&
							!dealerNotFound_Selected}
						class:is-valid={isValid && !loadingDealerInfo.loading && !autocompleteOpen}
						class:active={(isValid && !loadingDealerInfo.loading && !autocompleteOpen) ||
							(dealerListSelectEl?.value && dealerListSelectEl.value.length > 0)}
						on:input={() => {
							Value = '';
						}}
						on:blur={validate}
						autocomplete="off"
					/>
					{#if dealerListSelectEl?.value && dealerListSelectEl.value.length > 0}
						<button
							type="button"
							class="clear-button"
							on:click={resetDealerSelector}
							aria-label="Clear input"
						>
							<!-- MD3 Clear Icon -->
							<svg
								xmlns="http://www.w3.org/2000/svg"
								height="24"
								width="24"
								viewBox="0 0 24 24"
								fill="currentColor"
							>
								<path
									d="M15.5 12l4.25-4.25-1.25-1.25L14.25 10.75 10 6.5l-1.25 1.25L12.75 12l-4.25 4.25L10 17.5l4.25-4.25 4.25 4.25 1.25-1.25L15.5 12z"
								/>
							</svg>
						</button>
					{/if}
					{#if dealerListSelectEl?.disabled}
						<div class="input-overlay" on:click={resetDealerSelector} />
					{/if}
					<div class="form-notch">
						<div class="form-notch-leading" style="width: 9px;" />
						<div
							class="form-notch-middle"
							style="width: 105.6px;"
							bind:this={notchMiddleEl_dealer}
						/>
						<div class="form-notch-trailing" />
					</div>
					<label class="form-label" for="dealerListSelect" bind:this={labelEl_dealer}
						>{loadingDealerInfo.loading ? 'Searching' : 'Dealer Selector'}</label
					>
					<div class="autocomplete-custom-content" />
					<div class="invalid-tooltip hidden" id="dealerListSelectInfo">
						{dealerSelectErrMsg || 'Please select a dealer'}
					</div>
					<div class="msg-spacer3" />
				</div>
			</div>
		</div>
		<div class="fs-7" style="padding-top: 0px">
			{#if !showZipCodeError}
				{#if lastZipSearch && !loadingDealerInfo.loading && dealers && dealers.length == 0}
					<strong>No dealers found for this zipcode.</strong> {@html Config.NotFoundMessage}
				{:else if Config.NotFoundMessage}
					{@html Config.NotFoundMessage}
				{/if}
			{/if}
		</div>
	</div>
	<div class:hidden={!dealerNotFound_Selected} />
</FormGroup>

<!-- {#if showAddressInput}
	<div class="hidden" id="dealer_address">
		<div class="form-group row g-2" id="dealer_address_row">
			<div class="title3">Address:</div>
			<div class="col-12">
				<div class="col">
					<div class="col form-outline">
						<input class="form-control" id="dealer_name" type="text" name="dealer_name" />
						<label class="form-label" for="dealer_name">Dealer Name</label>
					</div>
				</div>
			</div>
			<div class="col-12">
				<div class="col">
					<div class="form-outline autocomplete" id="dealer_streetiv_d">
						<input
							class="form-control"
							id="dealer_street"
							type="text"
							name="dealer_street"
							onfocus="this.value = this.value;"
						/>
						<label class="form-label" id="streetLbl_d" for="dealer_street">Street Address</label>
						<div class="invalid-tooltip hidden" id="streetInfo_d" />
					</div>
				</div>
			</div>
			<div class="col-10 col-md-7">
				<div class="col form-outline">
					<input class="form-control" id="city_d" type="text" name="city" disabled="" />
					<label class="form-label" for="city_d">City</label>
				</div>
			</div>
			<div class="col-2 col-md-2">
				<div class="col form-outline">
					<input class="form-control" id="state_d" type="text" name="state" disabled="" />
					<label class="form-label" for="state_d">State</label>
				</div>
			</div>
			<div class="col-md-3 col-sm">
				<div class="col form-outline">
					<input class="form-control" id="zipcode_d" type="text" name="zipcode" disabled="" />
					<label class="form-label" for="zipcode_d">Zip Code</label>
				</div>
			</div>
		</div>
	</div>
{/if} -->
<style>
	.input-container {
		position: relative;
		width: 100%;
	}

	.input-with-clear {
		width: 100%;
		padding-right: 2.5rem; /* Adjust to accommodate the button width */
	}

	.clear-button {
		position: absolute;
		top: 50%;
		right: 0.5rem; /* Adjust the horizontal position */
		transform: translateY(-50%);
		background: none;
		border: none;
		padding: 0;
		margin: 0;
		cursor: pointer;
		color: currentColor;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.clear-button:hover {
		color: #2c00ee; /* MD3 primary color or your chosen hover color */
	}

	/* Optional: Adjust the input field focus outline to match MD3 */
	.form-control:focus {
		border-color: #2c00ee;
		box-shadow: 0 1px 0 0 #2c00ee;
	}
	.input-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		cursor: pointer;
		/* Optional: Add a visual cue that the field is interactive */
		/* background-color: rgba(0, 0, 0, 0.05); */
	}
</style>
